import { Button, Modal, Space, Spin, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationFee from '~Api/Application/IApplicationFee';
import {
    applicationFeeDeleteAction,
    applicationFeesListAction,
    applicationGetAction,
} from '~Applications/actions';
import {
    applicationFeesSelector,
    applicationSelector,
} from '~Applications/selectors';
import { IGlobalState } from '~reducer';
import FeeAddModal from './FeeAddModal';
import FeeEditModal from './FeeEditModal';
import Layout from './Layout';
import { ColumnType } from 'antd/lib/table';
import { IDictionary } from '~utilities/IDictionary';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { currencyFormatter } from '~utilities/formatters';

export default function Fees(): ReactElement {
    const { applicationUuid } = useParams<{ applicationUuid: string }>();

    const [ editFeeUuid, setEditFeeUuid ] = useState<string>(null);
    const [ isAddModalOpen, setIsAddModalOpen ] = useState<boolean>(false);

    const application: IApplication = useSelector((state: IGlobalState) => applicationSelector(state, applicationUuid));
    const fees: IDictionary<IApplicationFee> = useSelector((state: IGlobalState) => applicationFeesSelector(state, applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect(() => {
        if (!fees) {
            dispatch(applicationFeesListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        dispatch,
        fees,
    ]);

    const onClickAdd: () => void = useCallback(() => {
        setIsAddModalOpen(true);
    }, []);

    const onCloseAddModal: () => void = useCallback(() => {
        setIsAddModalOpen(false);
    }, []);

    const onCloseEditModal: () => void = useCallback(() => {
        setEditFeeUuid(null);
    }, []);

    const summary: () => JSX.Element = useCallback(() => {
        const total: number = _.reduce(fees, (sum: number, loopFee: IApplicationFee) => sum + loopFee.amount, 0);

        const isApplicationSettledOrClosed: boolean = (WorkflowStatusEnum.Warehoused === application.workflowStatus || null !== application.closeReason);
        const actionsCellBlock: JSX.Element = !isApplicationSettledOrClosed && (
            <Table.Summary.Cell index={3} />
        );

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2}><strong>{currencyFormatter.format(total)}</strong></Table.Summary.Cell>
                {actionsCellBlock}
            </Table.Summary.Row>
        );
    }, [
        application,
        fees,
    ]);

    if (!application || !fees) {
        return (
            <Layout applicationUuid={applicationUuid} section='fees'>
                <Typography.Title level={2}>Fees &amp; Outlays</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const isApplicationSettledOrClosed: boolean = (WorkflowStatusEnum.Warehoused === application.workflowStatus || null !== application.closeReason);

    const columns: ColumnType<IApplicationFee>[] = [
        {
            dataIndex: 'createdTime',
            render: (createdTime: string) => dayjs(createdTime).format('D/M/YYYY'),
            title: 'Date',
            width: '10%',
        },
        {
            dataIndex: 'description',
            title: 'Description',
        },
        {
            dataIndex: 'amount',
            render: (amount: number) => currencyFormatter.format(amount),
            title: 'Amount',
            width: '15%',
        },
        ...(!isApplicationSettledOrClosed ? [
            {
                render: (fee: IApplicationFee) => {
                    const onClickEdit: () => void = () => setEditFeeUuid(fee.uuid);

                    const onClickDelete: () => void = () => {
                        Modal.confirm({
                            content: 'Are you sure you want to delete this fee/outlay?',
                            okText: 'Delete',
                            okType: 'danger',
                            onOk: () => {
                                dispatch(applicationFeeDeleteAction(fee.uuid));
                            },
                            title: 'Delete',
                        });
                    };

                    return (
                        <Space>
                            <Button onClick={onClickEdit}>Edit</Button>
                            <Button danger={true} onClick={onClickDelete}>Delete</Button>
                        </Space>
                    );
                },
                title: 'Actions',
                width: '15%',
            },
        ] : []),
    ];

    const addBlock: JSX.Element = !isApplicationSettledOrClosed && (
        <Button onClick={onClickAdd}>
            Add Fee/Outlay
        </Button>
    );

    return (
        <Layout applicationUuid={applicationUuid} section='fees'>
            <Space className='actions'>
                {addBlock}
            </Space>
            <Typography.Title level={2}>Fees &amp; Outlays</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.sortBy(fees, ['createdTime'])}
                rowKey='uuid'
                pagination={false}
                size='middle'
                summary={summary}
            />
            <FeeEditModal
                feeUuid={editFeeUuid}
                applicationUuid={application.uuid}
                isOpen={!!editFeeUuid}
                onClose={onCloseEditModal}
            />
            <FeeAddModal
                applicationUuid={application.uuid}
                isOpen={isAddModalOpen}
                onClose={onCloseAddModal}
            />
        </Layout>
    );
}
